/**
 * MODULE: domain/pages/frontend/components/editor/pagesBlockAddMenu.ts
 * PURPOSE: DOM rendering for Notion-style block add (+) menu on the block handle toolbar
 * AGENT SAFETY: 🟢 SAFE
 * RECENT: 2026-05-24 [2.2.1186] — dedicated scroll region; wheel trap; slash-style item rows
 */

import type { Editor } from '@tiptap/react';
import {
  applyBlockTransform,
  getAvailableBlockTransforms,
  type BlockTransformId,
} from '../../utils/blockTransformUtils';
import {
  insertBlockBelowTarget,
  type BlockAddCommandCallbacks,
} from '../../utils/blockInsertUtils';
import { isNestedDraggableTarget, type DraggableBlockTarget } from '../../utils/pagesBlockDragTarget';
import { SLASH_COMMANDS, type SlashCommandId } from '../../utils/slashCommands';
import {
  groupSlashCommands,
  SLASH_COMMAND_GROUP_LABELS,
  type SlashCommandGroupId,
} from '../../utils/slashCommandGroups';

const MENU_SHELL_CLASS =
  'pages-block-add-menu min-w-[15rem] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900';

const MENU_SCROLL_CLASS =
  'pages-block-add-menu-scroll max-h-[min(20rem,55vh)] overflow-y-auto overscroll-y-contain scroll-py-1 py-1 touch-pan-y [scrollbar-width:thin] [scrollbar-color:rgb(156_163_175)_transparent] dark:[scrollbar-color:rgb(75_85_99)_transparent]';

const MENU_ITEM_CLASS =
  'flex w-full flex-col items-start px-3 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800';

const MENU_ITEM_LABEL_CLASS = 'text-sm font-medium text-gray-800 dark:text-gray-100';

const MENU_ITEM_DESC_CLASS = 'text-xs text-gray-500 dark:text-gray-400';

const MENU_GROUP_CLASS =
  'sticky top-0 z-[1] bg-white px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:bg-gray-900 dark:text-gray-500';

export type BlockAddMenuPanel = {
  shell: HTMLDivElement;
  scroll: HTMLDivElement;
};

function trapScrollWheel(scroll: HTMLElement): void {
  scroll.addEventListener(
    'wheel',
    (event) => {
      event.stopPropagation();
      const delta = event.deltaY;
      const atTop = scroll.scrollTop <= 0;
      const atBottom = scroll.scrollTop + scroll.clientHeight >= scroll.scrollHeight - 1;
      if ((delta < 0 && atTop) || (delta > 0 && atBottom)) {
        event.preventDefault();
      }
    },
    { passive: false }
  );

  scroll.addEventListener(
    'touchmove',
    (event) => {
      event.stopPropagation();
    },
    { passive: true }
  );
}

/** Keep scroll inside the menu instead of bubbling to the ProseMirror editor. */
export function configureBlockAddMenuScroll(scroll: HTMLElement): void {
  trapScrollWheel(scroll);
}

export function createBlockAddMenuPanel(): BlockAddMenuPanel {
  const shell = document.createElement('div');
  shell.className = MENU_SHELL_CLASS;
  shell.setAttribute('data-testid', 'pages-block-add-menu');
  shell.setAttribute('role', 'menu');

  const scroll = document.createElement('div');
  scroll.className = MENU_SCROLL_CLASS;
  scroll.setAttribute('data-testid', 'pages-block-add-menu-scroll');
  scroll.tabIndex = 0;
  shell.append(scroll);

  configureBlockAddMenuScroll(scroll);

  return { shell, scroll };
}

function createMenuItem(
  label: string,
  description: string | undefined,
  testId: string,
  onSelect: () => void
): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = MENU_ITEM_CLASS;
  button.setAttribute('role', 'menuitem');
  button.setAttribute('data-testid', testId);

  const labelEl = document.createElement('span');
  labelEl.className = MENU_ITEM_LABEL_CLASS;
  labelEl.textContent = label;
  button.append(labelEl);

  if (description) {
    const descEl = document.createElement('span');
    descEl.className = MENU_ITEM_DESC_CLASS;
    descEl.textContent = description;
    button.append(descEl);
  }

  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    onSelect();
  });

  return button;
}

export function renderBlockAddMenu(
  editor: Editor,
  scroll: HTMLDivElement,
  target: DraggableBlockTarget,
  callbacks: BlockAddCommandCallbacks,
  onClose: () => void
): void {
  scroll.replaceChildren();
  scroll.scrollTop = 0;
  const doc = editor.state.doc;
  const nested = isNestedDraggableTarget(doc, target);

  const grouped = groupSlashCommands(SLASH_COMMANDS);
  for (const group of grouped) {
    const label = document.createElement('p');
    label.className = MENU_GROUP_CLASS;
    label.textContent = SLASH_COMMAND_GROUP_LABELS[group.group as SlashCommandGroupId];
    scroll.append(label);

    for (const command of group.commands) {
      scroll.append(
        createMenuItem(command.label, command.description, `pages-block-add-${command.id}`, () => {
          insertBlockBelowTarget(editor, target, command.id as SlashCommandId, callbacks);
          onClose();
        })
      );
    }
  }

  const transformOptions = getAvailableBlockTransforms(target.node, nested);
  if (transformOptions.length > 0) {
    const divider = document.createElement('div');
    divider.className = 'my-1 border-t border-gray-100 dark:border-gray-800';
    scroll.append(divider);

    const transformHeading = document.createElement('p');
    transformHeading.className = MENU_GROUP_CLASS;
    transformHeading.textContent = 'Turn into';
    scroll.append(transformHeading);

    for (const option of transformOptions) {
      scroll.append(
        createMenuItem(option.label, undefined, `pages-block-transform-${option.id}`, () => {
          applyBlockTransform(editor, target, option.id as BlockTransformId, nested);
          onClose();
        })
      );
    }
  }
}
